





















































import { defineComponent, toRefs, ref, computed, PropType, toRef, watch } from '@vue/composition-api';

import AppCard from '@berlin-front/common/components/elements/AppCard';
import AppContainer from '@berlin-front/common/components/elements/AppContainer';
import AppLink from '@berlin-front/common/components/elements/AppLink';
import AppProgressBar from '@berlin-front/common/components/elements/AppProgressBar';
import AppRow from '@berlin-front/common/components/elements/AppRow';
import AppText from '@berlin-front/common/components/elements/AppText';

import AuthCodeInput from '@berlin-front/common/components/clusters/AuthCodeInput';
import FlashMessage from '@berlin-front/common/components/clusters/FlashMessage';
import ModalHeader from '@berlin-front/common/components/clusters/ModalHeader';

import { useContext } from '@berlin-front/common/composables/context';
import { useRoute } from '@berlin-front/common/composables/useRoute';
import { ModalHeaderContext } from '@services/ModalHeaderContext';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useAtoneLogout from '@services/useAtoneLogout';

import { HEADER_TYPE } from '@berlin-front/common/components/clusters/ModalHeader/composable';
import { ALERT_TYPE } from '@berlin-front/common/components/elements/AppAlert/composable';
import { TEXT_WEIGHT } from '@berlin-front/common/components/elements/AppText/composable';
import { HEIGHT_TYPE } from '@berlin-front/common/components/elements/AppProgressBar/composable';
import { RouteName, ROUTE_NAME } from '@/router/route-name';
import { bemx, noOperation, notNil, formatTelNumber } from '@berlin-front/common/utils';
import { SmsAuthContext } from './context';

export default defineComponent({
  name: 'SmsAuthView',
  components: {
    AppCard,
    AppContainer,
    AppLink,
    AppProgressBar,
    AppRow,
    AppText,
    AuthCodeInput,
    FlashMessage,
    ModalHeader,
    HeaderContextConsumer: ModalHeaderContext.consumer,
  },

  props: {
    /**
     * 遷移元のroute名。クエリパラメータpreviousで指定する。
     */
    previous: {
      type: String as PropType<RouteName>,
      required: false,
      default: '',
    },

    /**
     * 前ページに遷移する前にログアウトするかどうか。
     */
    logout: {
      type: String as PropType<'true' | undefined>,
      default: undefined,
    },
  },

  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const { useSmsAuth } = useContext(SmsAuthContext);
    const { logout } = useAtoneLogout();
    /** 戻るボタンを押した時の遷移先のRouteName */
    const previous = toRef(props, 'previous');
    /** 前画面に遷移する前にログアウトするかどうか。戻るがログアウトを意味するような時につかう。 */
    const logoutBeforePreviousTransit = computed(() => props.logout === 'true');

    const authCodeRef = ref();

    const {
      // 表示項目
      phoneNumber,
      errorMessages,
      infoMessages,
      description,

      // progress
      progress,

      // loading
      isLoadingApi,

      // action
      backToPrevious,
      backToRegister,
      onSubmitVerify,
      onSubmitResend,
    } = toRefs(useSmsAuth(authCodeRef));

    const backToRegisterIfIdle = computed(() =>
      backToRegister?.value !== undefined && !isLoadingApi.value ? backToRegister.value : () => {},
    );
    const hasBackToRegister = computed(() => backToRegister?.value !== undefined);

    // 戻るおよびそれに付随したログアウト処理。
    const backToPreviousOrNoop = computed(() => {
      if (backToPrevious?.value) {
        return backToPrevious.value;
      }
      if (previous.value) {
        return () => {
          if (logoutBeforePreviousTransit.value) {
            logout({
              onLogout: () => {
                void router.replace({ name: previous.value });
              },
            });
          } else {
            void router.replace({
              name: previous.value,
            });
          }
        };
      }
      return noOperation;
    });

    const hasBackToPrevious = computed(() => backToPreviousOrNoop.value !== noOperation);

    // ローディング中はcssでviews内のマウスイベントをぶち殺す。
    const className = computed(() => bemx('SmsAuthView', { loading: isLoadingApi.value }));

    // ローディング中はキャプチャフェーズでマウスとキーイベントをぶち殺す。
    const screenRef = ref<HTMLDivElement>();
    watch([screenRef, isLoadingApi] as const, ([element, isLoading], _old, onInvalidate) => {
      if (element && isLoading) {
        const listener = (ev: Event) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        element.addEventListener('keydown', listener, { capture: true });
        onInvalidate(() => {
          element.removeEventListener('keydown', listener, { capture: true });
        });
      }
    });

    return {
      screenRef,

      // AuthCodeInputの参照
      authCodeRef,

      // 表示項目
      errorMessages,
      infoMessages,
      phoneNumber,
      description,
      hasBackToRegister,
      hasBackToPrevious,
      className,

      // 関数
      backToPrevious,
      backToRegisterIfIdle,
      backToPreviousOrNoop,
      onSubmitVerify,
      onSubmitResend,
      formatTelNumber,

      // 定数値
      HEADER_TYPE,
      ALERT_TYPE,
      TEXT_WEIGHT,
      HEIGHT_TYPE,
      ROUTE_NAME,

      // route
      route,

      // progress
      progress,

      // loading
      isLoadingApi,

      notNil,
    };
  },
});
